import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Book,
  Lock,
  FileText,
  Globe,
  Mail,
  CreditCard,
  Users,
  ShieldCheck,
  Gavel,
} from "lucide-react";

const TermsOfService = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const Section = ({ id, title, icon: Icon, children }) => (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-6 hover:bg-orange-50 transition-colors text-left"
      >
        <div className="flex items-center gap-4">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Icon className="w-6 h-6 text-orange-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        </div>
        {expandedSections[id] ? (
          <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      {expandedSections[id] && <div className="px-6 pb-6">{children}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-sm p-8 border-b border-gray-200">
          <div className="text-left">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Lock className="w-8 h-8 text-orange-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">
                  TableCheck Inc. — Terms of Service
                </h1>
                <p className="text-gray-600">
                  Last Updated: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-b-xl shadow-sm divide-y divide-gray-200">
          <Section id="introduction" title="1. Introduction" icon={Book}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>
                Welcome to <strong>Hiree</strong>, a service provided by <strong>TableCheck Inc.</strong> ("TableCheck," "we," "us," or "our"). These Terms govern your access and use of our platform, including our website, mobile application, and related services.
              </p>
              <p>
                By accessing or using our Services, you <strong>agree to comply with these Terms</strong>. If you do not agree, you may <strong>not use</strong> our Services.
              </p>
            </div>
          </Section>

          <Section id="account" title="2. Account Registration & Security" icon={Users}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>To use certain features, you must create an account and agree to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Provide <strong>accurate, current, and complete</strong> information.</li>
                <li>Maintain the <strong>confidentiality</strong> of your login credentials.</li>
                <li>Immediately <strong>notify us</strong> of any unauthorized access.</li>
              </ul>
              <p>
                You are <strong>fully responsible</strong> for all activities under your account, and we <strong>are not liable</strong> for any unauthorized use of your account.
              </p>
            </div>
          </Section>

          <Section id="use-of-services" title="3. Use of Services" icon={FileText}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>You agree <strong>not</strong> to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Engage in <strong>illegal, fraudulent, or deceptive</strong> activities.</li>
                <li>Post <strong>misleading, offensive, or prohibited content</strong>.</li>
                <li>Use the platform to <strong>harass, abuse, or defraud others</strong>.</li>
                <li>Attempt to <strong>hack, disrupt, or overload our systems</strong>.</li>
              </ul>
              <p>We reserve the right to <strong>suspend or terminate</strong> accounts that violate these rules.</p>
            </div>
          </Section>

          <Section id="payments" title="4. Payments & Fees" icon={CreditCard}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>Some Services require payment. By purchasing, you agree that:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>All transactions are <strong>processed securely</strong> via third-party providers (e.g., Stripe).</li>
                <li>Fees are <strong>non-refundable</strong> unless otherwise stated.</li>
                <li>We reserve the right to <strong>modify pricing with prior notice</strong>.</li>
              </ul>
            </div>
          </Section>

          <Section id="privacy" title="5. Privacy & Data Protection" icon={Globe}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>
                Your use of our Services is subject to our Privacy Policy, which outlines how we collect, use, and protect your data.
              </p>
            </div>
          </Section>

          <Section id="liability" title="6. Disclaimers & Liability" icon={ShieldCheck}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>To the extent permitted by law, TableCheck is <strong>not liable</strong> for:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Any <strong>indirect, incidental, or consequential damages</strong>.</li>
                <li>Loss of <strong>data, profits, or business opportunities</strong>.</li>
                <li>Any <strong>employment disputes</strong> between job seekers and employers.</li>
              </ul>
            </div>
          </Section>

          <Section id="governing-law" title="7. Governing Law & Dispute Resolution" icon={Gavel}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>
                These Terms are governed by the laws of Lewes, Delaware. Any disputes will be resolved through binding arbitration in Lewes, Delaware unless prohibited by law.
              </p>
            </div>
          </Section>

          <Section id="contact" title="8. Contact Us" icon={Mail}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>If you have questions, contact us at:</p>
              <div className="bg-orange-50 rounded-lg p-6">
                <p>📧 Email: <a href="mailto:privacy@tablecheck.com" className="text-orange-600 hover:text-orange-700">privacy@tablecheck.com</a></p>
                <p>📍 Address: 16192 Coastal Highway, Lewes, Delaware, 19958</p>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;